import * as React from 'react';
import { Droppable } from 'react-beautiful-dnd';

import EquipmentDraggable from 'af-root/scenes/Company/ScheduleBoard/Shared/EquipmentDraggable';

import { ScheduleBoardProperty } from '@acceligentllc/shared/enums/scheduleBoardProperty';

interface Props {
	droppableId: string;
	draggablePrefix: string; // to distinguish if it's board or toolbar dragged equipment
	equipmentIds: number[];
	isToolbar: boolean;
	isDisabled: boolean;
	className: string;
	startIndex: number;
	endIndex: number;
	title?: string;
	dueDate: string;
	isWorkOrderCanceled?: boolean;
	isCopyPlaceholder?: boolean;
	isCardDisabled?: boolean;
	hasReason?: boolean;
	isDragAndDropDisabled: boolean;
	hasPermissionsToEditScheduleBoard: boolean;
}

const DroppableComponent: React.FC<Props> = (props) => {
	const {
		title,
		droppableId,
		startIndex,
		endIndex,
		isDisabled,
		equipmentIds: _equipmentIds,
		className,
		isCopyPlaceholder,
		isDragAndDropDisabled,
		hasPermissionsToEditScheduleBoard,
		draggablePrefix,
		isToolbar,
		dueDate,
		isCardDisabled,
		isWorkOrderCanceled,
		hasReason,
	} = props;

	const equipmentIds = React.useMemo(() => _equipmentIds.slice(startIndex, endIndex), [_equipmentIds, endIndex, startIndex]);

	const renderEquipment = React.useCallback((ids: number[], _startIndex: number) => {
		return ids.map((_id: number, _index: number) => {
			return (
				<EquipmentDraggable
					draggableId={`${draggablePrefix}#${ScheduleBoardProperty.EQUIPMENT}#${dueDate}#${_id}`}
					droppableId={droppableId}
					dueDate={dueDate}
					equipmentId={_id}
					hasPermissionsToEditScheduleBoard={hasPermissionsToEditScheduleBoard}
					hasReason={hasReason}
					index={_startIndex + _index}
					isCardDisabled={isCardDisabled}
					isCopyPlaceholder={isCopyPlaceholder}
					isDragAndDropDisabled={isDragAndDropDisabled}
					isToolbar={isToolbar}
					isWOLocked={false}
					isWorkOrderCanceled={isWorkOrderCanceled}
					key={`${draggablePrefix}#${ScheduleBoardProperty.EQUIPMENT}#${dueDate}#${_id}`}
				/>
			);
		});
	}, [
		draggablePrefix,
		droppableId,
		dueDate,
		hasPermissionsToEditScheduleBoard,
		hasReason,
		isCardDisabled,
		isCopyPlaceholder,
		isDragAndDropDisabled,
		isToolbar,
		isWorkOrderCanceled,
	]);

	const renderDraggable = React.useCallback((provided, snapshot) => {
		return (
			<div
				className={`${className} ${snapshot.isDraggingOver ? 'drag-over' : ''}`}
				ref={provided.innerRef}
			>
				{title && <label className="droppable-title">{title}</label>}
				{renderEquipment(equipmentIds, startIndex)}
				{provided.placeholder}
			</div>
		);
	}, [className, equipmentIds, renderEquipment, startIndex, title]);

	if (isCopyPlaceholder || isDragAndDropDisabled || !hasPermissionsToEditScheduleBoard) {
		return (
			<div className={className}>
				{title && <label className="droppable-title">{title}</label>}
				{renderEquipment(equipmentIds, startIndex)}
			</div>
		);
	}

	return (
		<Droppable
			droppableId={droppableId}
			isDropDisabled={isDisabled}
		>
			{renderDraggable}
		</Droppable>
	);
};

export default React.memo(DroppableComponent);
